import { FaHotel, FaHome, FaBuilding, FaUmbrellaBeach, FaCity, FaBed, FaPlus } from 'react-icons/fa';

interface AccommodationDetails {
    name?: string;
    city?: string;
    type?: string;
}

interface AccommodationData {
    id?: string;
    name?: string;
    type: string;
    city: string;
    address?: string;
    phone?: string;
    url?: string;
    checkInDate?: string;
    checkOutDate?: string;
    roomType?: string;
    bookingAgent?: string;
    bookingUrl?: string;
    bookingReference?: string;
    cost?: {
        amount?: number;
        currency?: string;
        paid?: boolean;
    };
}

// Accommodation type definitions with UI metadata and validation rules
export const ACCOMMODATION_TYPES = {
  hotel: {
    icon: FaHotel,
    label: 'Hotel',
    fields: {
      name: { required: false, type: 'text', label: 'Hotel Name' },
      city: { required: true, type: 'text', label: 'City' },
      address: { required: false, type: 'text', label: 'Address' },
      phone: { required: false, type: 'text', label: 'Phone Number' },
      url: { required: false, type: 'url', label: 'Hotel Website' },
      checkInDate: { required: false, type: 'datetime', label: 'Check-in Date/Time' },
      checkOutDate: { required: false, type: 'datetime', label: 'Check-out Date/Time' },
      roomType: { required: false, type: 'text', label: 'Room Type' },
      bookingAgent: { required: false, type: 'text', label: 'Booking Agent' },
      bookingUrl: { required: false, type: 'url', label: 'Booking URL' },
      bookingReference: { required: false, type: 'text', label: 'Booking Reference' }
    },
    createSummary: (details: AccommodationDetails) => {
      if (details.name && details.city) {
        return `${details.name}: ${details.city}`;
      } else if (details.name) {
        return details.name;
      } else if (details.city) {
        return `Accommodation in ${details.city}`;
      } else {
        return 'Accommodation';
      }
    }
  },
  apartment: {
    icon: FaBuilding,
    label: 'Apartment',
    fields: {
      name: { required: false, type: 'text', label: 'Apartment Name' },
      city: { required: true, type: 'text', label: 'City' },
      address: { required: false, type: 'text', label: 'Address' },
      phone: { required: false, type: 'text', label: 'Phone Number' },
      url: { required: false, type: 'url', label: 'Apartment Website' },
      checkInDate: { required: false, type: 'datetime', label: 'Check-in Date/Time' },
      checkOutDate: { required: false, type: 'datetime', label: 'Check-out Date/Time' },
      propertyType: { required: false, type: 'text', label: 'Property Type' },
      bookingAgent: { required: false, type: 'text', label: 'Booking Agent' },
      bookingUrl: { required: false, type: 'url', label: 'Booking URL' },
      bookingReference: { required: false, type: 'text', label: 'Booking Reference' }
    },
    createSummary: (details: AccommodationDetails) => {
      if (details.name && details.city) {
        return `${details.name}: ${details.city}`;
      } else if (details.name) {
        return details.name;
      } else if (details.city) {
        return `Apartment in ${details.city}`;
      } else {
        return 'Apartment';
      }
    }
  },
  house: {
    icon: FaHome,
    label: 'House/Villa',
    fields: {
      name: { required: false, type: 'text', label: 'Property Name' },
      city: { required: true, type: 'text', label: 'City' },
      address: { required: false, type: 'text', label: 'Address' },
      phone: { required: false, type: 'text', label: 'Phone Number' },
      url: { required: false, type: 'url', label: 'Property Website' },
      checkInDate: { required: false, type: 'datetime', label: 'Check-in Date/Time' },
      checkOutDate: { required: false, type: 'datetime', label: 'Check-out Date/Time' },
      propertyType: { required: false, type: 'text', label: 'Property Type' },
      bookingAgent: { required: false, type: 'text', label: 'Booking Agent' },
      bookingUrl: { required: false, type: 'url', label: 'Booking URL' },
      bookingReference: { required: false, type: 'text', label: 'Booking Reference' }
    },
    createSummary: (details: AccommodationDetails) => {
      if (details.name && details.city) {
        return `${details.name}: ${details.city}`;
      } else if (details.name) {
        return details.name;
      } else if (details.city) {
        return `House in ${details.city}`;
      } else {
        return 'House/Villa';
      }
    }
  },
  resort: {
    icon: FaUmbrellaBeach,
    label: 'Resort',
    fields: {
      name: { required: false, type: 'text', label: 'Resort Name' },
      city: { required: true, type: 'text', label: 'City' },
      address: { required: false, type: 'text', label: 'Address' },
      phone: { required: false, type: 'text', label: 'Phone Number' },
      url: { required: false, type: 'url', label: 'Resort Website' },
      checkInDate: { required: false, type: 'datetime', label: 'Check-in Date/Time' },
      checkOutDate: { required: false, type: 'datetime', label: 'Check-out Date/Time' },
      roomType: { required: false, type: 'text', label: 'Room Type' },
      boardType: { required: false, type: 'text', label: 'Board Type' },
      bookingAgent: { required: false, type: 'text', label: 'Booking Agent' },
      bookingUrl: { required: false, type: 'url', label: 'Booking URL' },
      bookingReference: { required: false, type: 'text', label: 'Booking Reference' }
    },
    createSummary: (details: AccommodationDetails) => {
      if (details.name && details.city) {
        return `${details.name}: ${details.city}`;
      } else if (details.name) {
        return details.name;
      } else if (details.city) {
        return `Resort in ${details.city}`;
      } else {
        return 'Resort';
      }
    }
  },
  hostel: {
    icon: FaBed,
    label: 'Hostel',
    fields: {
      name: { required: false, type: 'text', label: 'Hostel Name' },
      city: { required: true, type: 'text', label: 'City' },
      address: { required: false, type: 'text', label: 'Address' },
      phone: { required: false, type: 'text', label: 'Phone Number' },
      url: { required: false, type: 'url', label: 'Hostel Website' },
      checkInDate: { required: false, type: 'datetime', label: 'Check-in Date/Time' },
      checkOutDate: { required: false, type: 'datetime', label: 'Check-out Date/Time' },
      roomType: { required: false, type: 'text', label: 'Room Type' },
      bookingAgent: { required: false, type: 'text', label: 'Booking Agent' },
      bookingUrl: { required: false, type: 'url', label: 'Booking URL' },
      bookingReference: { required: false, type: 'text', label: 'Booking Reference' }
    },
    createSummary: (details: AccommodationDetails) => {
      if (details.name && details.city) {
        return `${details.name}: ${details.city}`;
      } else if (details.name) {
        return details.name;
      } else if (details.city) {
        return `Hostel in ${details.city}`;
      } else {
        return 'Hostel';
      }
    }
  },
  custom: {
    icon: FaPlus,
    label: 'Custom',
    fields: {
      name: { required: false, type: 'text', label: 'Accommodation Name' },
      type: { required: true, type: 'text', label: 'Accommodation Type' },
      city: { required: true, type: 'text', label: 'City' },
      address: { required: false, type: 'text', label: 'Address' },
      phone: { required: false, type: 'text', label: 'Phone Number' },
      url: { required: false, type: 'url', label: 'Website' },
      checkInDate: { required: false, type: 'datetime', label: 'Check-in Date/Time' },
      checkOutDate: { required: false, type: 'datetime', label: 'Check-out Date/Time' },
      details: { required: false, type: 'text', label: 'Additional Details' },
      bookingAgent: { required: false, type: 'text', label: 'Booking Agent' },
      bookingUrl: { required: false, type: 'url', label: 'Booking URL' },
      bookingReference: { required: false, type: 'text', label: 'Booking Reference' }
    },
    createSummary: (details: AccommodationDetails) => {
      if (details.name && details.city) {
        return `${details.name}: ${details.city}`;
      } else if (details.name) {
        return details.name;
      } else if (details.city) {
        return `${details.type || 'Accommodation'} in ${details.city}`;
      } else {
        return details.type || 'Custom Accommodation';
      }
    }
  }
};

// Common fields that apply to all accommodation types
export const ACCOMMODATION_FIELDS = {
  name: { required: false, type: 'text', label: 'Accommodation Name' },
  city: { required: true, type: 'text', label: 'City' },
  address: { required: false, type: 'text', label: 'Address' },
  phone: { required: false, type: 'text', label: 'Phone Number' },
  url: { required: false, type: 'url', label: 'Website URL' },
  checkInDate: { required: false, type: 'datetime', label: 'Check-in Date/Time' },
  checkOutDate: { required: false, type: 'datetime', label: 'Check-out Date/Time' },
  roomType: { required: false, type: 'text', label: 'Room Type' },
  bookingAgent: { required: false, type: 'text', label: 'Booking Agent' },
  bookingUrl: { required: false, type: 'url', label: 'Booking URL' },
  bookingReference: { required: false, type: 'text', label: 'Booking Reference' },
  cost: {
    amount: { required: false, type: 'number', label: 'Cost Amount' },
    currency: { required: false, type: 'text', label: 'Currency' },
    paid: { required: false, type: 'checkbox', label: 'Paid' }
  }
};

// Statuses for accommodation bookings
export const ACCOMMODATION_STATUSES = [
  'Planned',
  'Booked',
  'Confirmed',
  'Completed',
  'Cancelled'
];

// src/types/interfaces.ts
export interface DatabaseActions {
  saveAccommodation: (data: AccommodationData) => Promise<string>;
  getAccommodation: (id: string) => Promise<AccommodationData>;
  // ... other database actions
}

export interface AuthUser {
  uid: string;
  // ... other user properties
}
